const DEFAULT_ALLOWED_DOMAINS = [
  'youtube.com',
  'youtu.be',
  'spotify.com',
  'open.spotify.com',
  'instagram.com',
  'bandcamp.com',
  'soundcloud.com',
  'tiktok.com',
  'twitter.com',
  'x.com',
  'facebook.com',
  'fb.com',
  'vimeo.com',
  'mixcloud.com',
  'discogs.com',
  'last.fm',
  'lastfm.com',
];

// Referencia opcional (p. ej. sugerencias en admin); la Comunidad no filtra por dominio al publicar.
const COMMUNITY_EXTRA_DOMAINS = [
  'google.com',
  'maps.app.goo.gl',
  'goo.gl',
];

const CONTEXT_RULES = {
  username: { min: 2, max: 24, allowUrls: false },
  bio: { min: 0, max: 280, allowUrls: true },
  listing_title: { min: 2, max: 80, allowUrls: false },
  listing_description: { min: 0, max: 1000, allowUrls: true },
  /** Chat: solo texto y emoticonos; enlaces y archivos fuera del canal (SDD §4.B). */
  chat: { min: 1, max: 700, allowUrls: false },
  /** Enlaces libres: revisión en admin (humana / IA). */
  comunidad_title: { min: 3, max: 90, allowUrls: true, skipUrlAllowlist: true },
  comunidad_body: { min: 0, max: 900, allowUrls: true, skipUrlAllowlist: true },
  general: { min: 0, max: 1000, allowUrls: true },
};

// Lista deliberadamente corta: solo casos claros (insultos graves / spam obvio).
// Nota: mantenerlo simple para evitar falsos positivos.
const BANNED_PATTERN_SHORTLINK_SPAM = /\b(?:click\s*aquí|haz\s*click|bit\.ly|tinyurl\.com)\b/i;

const BANNED_PATTERNS = [
  // Spam / estafa obvia
  /\b(?:viagra|casino|apuesta(?:s)?|cript(?:o)?|bitcoin|btc|eth|airdrop|pump|telegram\.me|t\.me|onlyfans)\b/i,
  /\b(?:gana dinero|trabaja desde casa|ingresos pasivos|hazte rico)\b/i,
  BANNED_PATTERN_SHORTLINK_SPAM,

  // Insultos graves / odio (ES)
  /\b(?:hijo\s+de\s+puta|hijoputa|gilipollas|subnormal|maric[oó]n|puta|puto|mierda)\b/i,
  /\b(?:nazi|hitler)\b/i,
];

/** En Comunidad se permiten acortadores/enlaces arbitrarios; no aplicamos el patrón de bit.ly/tinyurl. */
const BANNED_PATTERNS_COMUNIDAD = BANNED_PATTERNS.filter(p => p !== BANNED_PATTERN_SHORTLINK_SPAM);

function normalizeText(t) {
  return String(t ?? '').replace(/\s+/g, ' ').trim();
}

function tooShortToModerate(text) {
  return normalizeText(text).length < 3;
}

function extractUrls(text) {
  const t = String(text ?? '');
  // Detecta http(s) y dominios típicos con o sin protocolo.
  const re = /(?:https?:\/\/)?(?:www\.)?[a-z0-9-]+\.[a-z]{2,}(?:\/[^\s<>"']*)?/gi;
  const matches = t.match(re) || [];
  // Evita capturar cosas como "a.b" sueltas en algunos casos: filtramos por tener un TLD mínimo.
  return matches
    .map(x => x.trim())
    .filter(Boolean)
    .slice(0, 20);
}

function hostnameFromLooseUrl(u) {
  const raw = String(u ?? '').trim();
  if (!raw) return null;
  const withProto = /^https?:\/\//i.test(raw) ? raw : `https://${raw.replace(/^\/+/, '')}`;
  try {
    const url = new URL(withProto);
    const host = String(url.hostname || '').toLowerCase();
    return host.startsWith('www.') ? host.slice(4) : host;
  } catch {
    return null;
  }
}

function isAllowedHost(host, allowedDomains) {
  if (!host) return false;
  const h = String(host).toLowerCase();
  return allowedDomains.some(d => h === d || h.endsWith(`.${d}`));
}

export function validateUrlsAgainstAllowlist(text, { allowedDomains = DEFAULT_ALLOWED_DOMAINS } = {}) {
  const urls = extractUrls(text);
  if (!urls.length) return { ok: true, urls: [], blocked: [] };
  const blocked = [];
  for (const u of urls) {
    const host = hostnameFromLooseUrl(u);
    if (!isAllowedHost(host, allowedDomains)) blocked.push(u);
  }
  return { ok: blocked.length === 0, urls, blocked };
}

/** Validación de mensaje de chat (texto + emoticonos; sin URLs ni HTML). */
export function moderateChatMessage(text) {
  const mm = moderateText(text, 'chat');
  if (!mm.approved) return mm;
  const clean = normalizeText(text);
  if (/<[^>]+>/.test(clean)) {
    return { approved: false, reason: 'No se permite HTML en el chat.' };
  }
  if (/(https?:\/\/|www\.)/i.test(clean)) {
    return { approved: false, reason: 'No incluyas enlaces en el chat; compártelos fuera de esta conversación.' };
  }
  if (/data:\s*image\//i.test(clean)) {
    return { approved: false, reason: 'No se permiten imágenes ni archivos adjuntos en el chat.' };
  }
  return { approved: true, reason: null };
}

export function moderateText(text, context = 'general') {
  const rules = CONTEXT_RULES[context] ?? CONTEXT_RULES.general;
  const clean = normalizeText(text);

  if (!clean) return { approved: true, reason: null };
  if (tooShortToModerate(clean)) return { approved: true, reason: null };

  if (clean.length < rules.min) {
    return { approved: false, reason: `Texto demasiado corto (mínimo ${rules.min} caracteres).` };
  }
  if (clean.length > rules.max) {
    return { approved: false, reason: `Texto demasiado largo (máximo ${rules.max} caracteres).` };
  }

  const bannedPatterns =
    context === 'comunidad_title' || context === 'comunidad_body' ? BANNED_PATTERNS_COMUNIDAD : BANNED_PATTERNS;
  for (const pat of bannedPatterns) {
    if (pat.test(clean)) return { approved: false, reason: 'El texto contiene términos no permitidos.' };
  }

  if (!rules.allowUrls) {
    const urlCheck = validateUrlsAgainstAllowlist(clean, { allowedDomains: [] });
    // Si hay cualquier URL, rechazamos (username/título).
    if (urlCheck.urls.length) return { approved: false, reason: 'No incluyas enlaces en este campo.' };
  } else if (!rules.skipUrlAllowlist) {
    const urlCheck = validateUrlsAgainstAllowlist(clean, { allowedDomains: DEFAULT_ALLOWED_DOMAINS });
    if (!urlCheck.ok) {
      return { approved: false, reason: 'Enlaces no permitidos. Usa solo plataformas musicales conocidas.' };
    }
  }
  // comunidad_* con skipUrlAllowlist: sin filtro de dominios (revisión en admin).

  return { approved: true, reason: null };
}

export const MODERATION_ALLOWED_DOMAINS = DEFAULT_ALLOWED_DOMAINS;
export const MODERATION_ALLOWED_DOMAINS_COMUNIDAD = [...DEFAULT_ALLOWED_DOMAINS, ...COMMUNITY_EXTRA_DOMAINS];
export const MODERATION_CONTEXT_RULES = CONTEXT_RULES;

